import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";

import { api_without_cancellation } from "../../../../api/api";
import useFirmAddress from "../../firm-address/hooks/useFirmAddress";
import { formatPhoneNumber } from "../../../../Utils/helper";

const labelStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "var(--primary-25)",
  marginBottom: "2px",
  display: "block",
};

const EditUserModal = ({ show, handleClose, userData, refetch }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [sbn, setSBN] = useState("");
  const [phoneNo, setPhone] = useState("");
  const [faxNo, setFaxNo] = useState("");
  const [ext, setExt] = useState("");
  const [homeOffId, setHomeOffId] = useState("");
  const [secondaryPhoneNumber, setSecondaryNumber] = useState("");
  // 'office' = use the selected home office's phone number + extension.
  // 'direct' = use the manually entered direct line below.
  // No backend field tracks the source, so it is derived from whether a
  // direct phone_number exists on the record at open time.
  const [phoneSource, setPhoneSource] = useState("office");
  const { firmAddress } = useFirmAddress();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userData) {
      const directLine = formatPhoneNumber(userData.phone_number);
      setFirstName(userData.user.first_name);
      setLastName(userData.user.last_name);
      setEmail(userData.user.email);
      setIsActive(userData.user.is_active ?? userData.is_active ?? false);
      setSaveError("");
      setSBN(userData.statebarnumber);
      setHomeOffId(userData.for_office?.id);
      setPhone(directLine);
      setFaxNo(formatPhoneNumber(userData.fax_number));
      setSecondaryNumber(formatPhoneNumber(userData?.secondary_phone_number));
      setExt(userData?.phone_extension);
      // Derive source: a saved direct line means the record was on 'direct'.
      setPhoneSource(userData?.phoneSource);
    }
  }, [userData]);

  const firmAdd = firmAddress?.firm_addresses?.filter((f) => f?.nickname !== null);
  const selectedOffice = firmAddress?.firm_addresses?.find((o) => o?.id === homeOffId);
  // Assumption: office phone number lives at `phone_number` on the firm
  // address record, same key as userData.phone_number. Adjust if your
  // useFirmAddress hook returns it under a different field.
  const officePhone = selectedOffice?.phone_number ?? "";

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    setSaveError("");
    let detailsSaved = false;
    try {
      const response = await api_without_cancellation.patch(
        `/api/firmsetting-page/attorneystaff/${userData.id}/edit/`,
        {
          first_name: firstName,
          last_name: lastName,
          email: email,
          statebarnumber: sbn,
          secondary_phone_number: secondaryPhoneNumber,
          for_officeId: homeOffId,
          user_phone: phoneNo,
          user_fax: faxNo,
          phone_extension: ext,
          phoneSource: phoneSource,
        }
      );
      if (response.status === 200) {
        detailsSaved = true;
        const wasActive = userData.user.is_active ?? userData.is_active ?? false;
        if (isActive !== wasActive) {
          await api_without_cancellation.post(
            "/api/firmsetting-page/update-useraccount-permissions/",
            { user_id: userData.user.id, is_active: isActive }
          );
        }
        handleClose();
        setFirstName("");
        setLastName("");
        setEmail("");
        setSBN("");
        setSecondaryNumber("");
        setExt("");
        setPhoneSource("office");
        refetch();
      }
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.detail ||
        "Unable to save the user. Please try again.";
      setSaveError(
        detailsSaved ? `User details saved, but status was not updated. ${message}` : message
      );
      if (detailsSaved) refetch();
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, "");
    let f = numbers;
    if (numbers.length > 3) {
      f = `(${numbers.slice(0, 3)})`;
      f += ` ${numbers.slice(3, 6)}`;
      if (numbers.length > 6) f += `-${numbers.slice(6, 10)}`;
    }
    return f;
  };

  const handleDirectLineChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  const homeOffRef = useRef(null);
  const [homeOffDropdown, setHomeOffDropdown] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (homeOffRef.current && !homeOffRef.current.contains(event.target)) {
        setHomeOffDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [homeOffRef]);

  return (
    <Modal
      dialogClassName="max-500 modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
      show={show}
      onHide={handleClose}
    >
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
      >
        <Modal.Title
          className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
          style={{ fontSize: "14px", fontWeight: "600" }}
        >
          Edit User
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "5px" }}>
        {saveError && (
          <div className="alert alert-danger p-2 mb-2" role="alert">
            {saveError}
          </div>
        )}

        <div className="row mx-0 align-items-center custom-margin-bottom">
          <div
            className="flex-g-1 p-0 d-flex align-items-center justify-content-center"
            style={{ gap: "5px" }}
          >
            <input
              id="editUserStatus"
              type="checkbox"
              className="cursor-pointer"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={loading}
            />
            <label htmlFor="editUserStatus" style={{ ...labelStyle, marginBottom: 0 }}>
              Active
            </label>
          </div>
        </div>
        {/* Office */}
        <div className="row mx-0 align-items-center custom-margin-bottom">
          <div ref={homeOffRef} className="flex-g-1 p-0 " style={{ position: "relative" }}>
            <label style={labelStyle}>Home Office</label>
            <div
              className="dropdown-button form-control custom-select-new-provider height-25 p-0 rounded-0 d-flex align-items-center"
              onClick={() => setHomeOffDropdown((prev) => !prev)}
              style={{ paddingLeft: "5px", cursor: "pointer", position: "relative" }}
            >
              {homeOffId ? (
                <span
                  className="d-flex align-items-center p-l-5"
                  style={{ height: "25px", fontSize: "16px", color: "black", fontWeight: "700" }}
                >
                  {firmAddress?.firm_addresses?.find((n) => n?.id === homeOffId)?.nickname}
                </span>
              ) : (
                <span
                  style={{ color: "var(--primary-25)", fontSize: "14px", fontWeight: "600" }}
                  className="p-l-5"
                >
                  Select Home Office
                </span>
              )}
            </div>
            {homeOffDropdown && (
              <div
                className="dropdown-menu pt-0 pb-0 mt-0 show"
                style={{
                  width: "100%",
                  border: "1px solid #ced4da",
                  scrollbarWidth: "none",
                  maxHeight: "315px",
                  fontSize: "16px",
                  overflowY: "auto",
                  zIndex: 1050,
                }}
              >
                {firmAdd?.map((type) => (
                  <div
                    key={type.id}
                    className="dropdown-option bg-speciality d-flex p-l-5 align-items-center height-25"
                    style={{
                      color: "black",
                      fontWeight: "700",
                      fontSize: "16px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setHomeOffId(type?.id);
                      setHomeOffDropdown(false);
                    }}
                  >
                    {type?.nickname}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* First Name | Last Name */}
        <div className="d-flex mx-0 align-items-center custom-margin-bottom" style={{ gap: "5px" }}>
          <div className="flex-g-1 p-0">
            <label htmlFor="editUserFirstName" style={labelStyle}>
              First Name
            </label>
            <input
              id="editUserFirstName"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{ color: "black" }}
              placeholder="Enter First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="flex-g-1 p-0">
            <label htmlFor="editUserLastName" style={labelStyle}>
              Last Name
            </label>
            <input
              id="editUserLastName"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{ color: "black" }}
              placeholder="Enter Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        {/* SBN | Secondary Phone */}
        <div className="d-flex mx-0 align-items-center custom-margin-bottom" style={{ gap: "5px" }}>
          <div className="flex-g-1 p-0">
            <label htmlFor="editUserSbn" style={labelStyle}>
              SBN
            </label>
            <input
              id="editUserSbn"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{ color: "black" }}
              placeholder="Enter SBN"
              value={sbn}
              onChange={(e) => setSBN(e.target.value)}
            />
          </div>
          <div className="flex-g-1 p-0">
            <label htmlFor="editUserSecondaryPhone" style={labelStyle}>
              Password Reset Texts to
            </label>
            <input
              id="editUserSecondaryPhone"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{ color: "black" }}
              placeholder="Enter Secondary Phone"
              value={secondaryPhoneNumber}
              onChange={(e) => setSecondaryNumber(formatPhone(e.target.value))}
            />
          </div>
        </div>

        {/* Office Phone Option */}
        <div className="row mx-0 align-items-center custom-margin-bottom">
          <div
            className="flex-g-1 p-0 d-flex align-items-center"
            style={{ gap: "8px", flexWrap: "wrap" }}
          >
            <input
              type="radio"
              id="phoneSourceOffice"
              name="phoneSource"
              checked={phoneSource === "office"}
              onChange={() => setPhoneSource("office")}
            />

            <label
              htmlFor="phoneSourceOffice"
              style={{
                ...labelStyle,
                marginBottom: 0,
                whiteSpace: "nowrap",
              }}
            >
              Office Phone
            </label>
            <span
              style={{
                color: officePhone ? "black" : "#6c757d",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              {officePhone || "Office Number"}
            </span>
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "var(--primary-25)",
              }}
            >
              Ext
            </span>

            <input
              id="editUserExt"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{
                width: "110px",
                maxWidth: "110px",
                color: "black",
              }}
              placeholder="Ext"
              value={ext}
              disabled={phoneSource !== "office"}
              onChange={(e) => setExt(e.target.value)}
            />
          </div>
        </div>

        {/* Direct Line Option */}
        <div className="row mx-0 align-items-center custom-margin-bottom">
          <div className="flex-g-1 p-0 d-flex align-items-center" style={{ gap: "8px" }}>
            <input
              type="radio"
              id="phoneSourceDirect"
              name="phoneSource"
              checked={phoneSource === "direct"}
              disabled={!phoneNo.trim()}
              onChange={() => setPhoneSource("direct")}
            />

            <label
              htmlFor="phoneSourceDirect"
              style={{
                ...labelStyle,
                marginBottom: 0,
                whiteSpace: "nowrap",
              }}
            >
              Direct Line
            </label>

            <input
              id="editUserDirectLine"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{ color: "black" }}
              placeholder="Enter Direct Line"
              value={phoneNo}
              onChange={handleDirectLineChange}
            />
          </div>
        </div>

        {/* Fax */}
        <div className="row mx-0 align-items-center custom-margin-bottom">
          <div className="flex-g-1 p-0">
            <label htmlFor="editUserFax" style={labelStyle}>
              Fax
            </label>
            <input
              id="editUserFax"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{ color: "black" }}
              placeholder="Enter Fax"
              value={faxNo}
              onChange={(e) => setFaxNo(formatPhone(e.target.value))}
            />
          </div>
        </div>

        {/* Email */}
        <div className="row mx-0 align-items-center custom-margin-bottom">
          <div className="flex-g-1 p-0">
            <label htmlFor="editUserEmail" style={labelStyle}>
              Email
            </label>
            <input
              id="editUserEmail"
              type="text"
              className="form-control height-25 p-0 p-l-5 rounded-0"
              style={{ color: "black" }}
              placeholder="Enter Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider"
        style={{ borderTop: "none" }}
      >
        <Button
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          variant="secondary"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleSubmit}
          disabled={loading}
        >
          Save and Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditUserModal;
